import React, { useState } from 'react';
import toast from 'react-hot-toast';
import Button from './Button';
import supabase from '../lib/supabaseClient';
import quoteService from '../services/quoteService';

const CTA = () => {
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [name, setName] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);

  // Format phone number to E.164 format
  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const cleaned = value.replace(/\D/g, '');

    // Add +1 for US numbers if not present
    if (cleaned.length === 10) {
      return `+1${cleaned}`;
    }

    // Already has country code
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return `+${cleaned}`;
    }

    // Already formatted or has + prefix
    if (value.startsWith('+')) {
      return value;
    }

    return value;
  };

  const handleGetQuote = async () => {
    if (!phoneNumber || phoneNumber.replace(/\D/g, '').length < 10) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }

    setLoading(true);

    try {
      const formattedPhone = formatPhoneNumber(phoneNumber);
      const result = await quoteService.sendOTP(formattedPhone);

      if (result.success) {
        setPhoneNumber(formattedPhone);
        setShowOtpModal(true);
      } else {
        toast.error(result.error || 'Failed to send OTP. Please try again.');
      }
    } catch (error) {
      toast.error('Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!name.trim()) {
      toast.error('Please enter your name');
      return;
    }

    if (otp.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }

    setLoading(true);

    try {
      const verifyResult = await quoteService.verifyOTP(phoneNumber, otp);

      if (!verifyResult.success) {
        toast.error('Invalid OTP. Please try again.');
        setLoading(false);
        return;
      }

      const { error: dbError } = await supabase
        .from('instant_leads')
        .insert([{ name: name.trim(), phone: phoneNumber, status: 'pending' }])
        .select();

      if (dbError) throw dbError;

      setShowOtpModal(false);
      setOtp('');
      setName('');
      setPhoneNumber('');
      setShowSuccessModal(true);
    } catch (error) {
      toast.error(`Failed to submit request: ${error.message || 'Please try again.'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowOtpModal(false);
    setOtp('');
  };

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
  };

  return (
    <section className="bg-slate-950 py-14 sm:py-16">
      <div className="container mx-auto px-4">
        <div className="mx-auto grid max-w-6xl gap-8 rounded-3xl border border-white/10 bg-white/[0.07] p-6 text-white shadow-[0_14px_42px_rgba(0,0,0,0.22)] backdrop-blur md:p-8 lg:grid-cols-[1fr_0.82fr] lg:items-center">
          <div>
            <span className="inline-flex rounded-full bg-primary/15 px-4 py-2 text-sm font-black text-orange-100">
              Free quote matching
            </span>
            <h2 className="mt-5 text-3xl font-black tracking-normal sm:text-4xl lg:text-5xl">
              Ready to plan your move?
            </h2>
            <p className="mt-4 max-w-2xl text-lg leading-8 text-slate-300">
              Get matched with trusted movers today. Start with your phone number and we will send a quick verification code.
            </p>
          </div>

          <div className="rounded-2xl bg-white p-4 text-slate-950 shadow-[0_12px_34px_rgba(0,0,0,0.16)] md:p-5">
            <label className="mb-2 block text-sm font-black">Start with your phone number</label>
            <div className="flex flex-col gap-3">
              <div className="flex overflow-hidden rounded-xl border border-slate-200 bg-white focus-within:border-primary focus-within:ring-4 focus-within:ring-primary/10">
                <span className="flex items-center border-r border-slate-200 bg-slate-50 px-3 text-sm font-black text-slate-700 select-none">+1</span>
                <input
                  type="tel"
                  placeholder="555 333 3333"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  disabled={loading}
                  maxLength={10}
                  className="min-w-0 flex-1 px-4 py-3 text-slate-900 focus:outline-none disabled:bg-slate-100 disabled:cursor-not-allowed"
                />
              </div>
              <Button
                variant="primary"
                className="w-full justify-center text-base"
                onClick={handleGetQuote}
                disabled={loading}
              >
                {loading ? 'Sending OTP...' : 'Request a Free Quote'}
                {!loading && (
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* OTP Modal */}
      {showOtpModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 relative">
            <button
              onClick={handleCloseModal}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl leading-none"
            >
              ×
            </button>

            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-100 rounded-full mb-4">
                <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Verify OTP</h3>
              <p className="text-sm text-gray-600">
                OTP sent to <span className="font-semibold">{phoneNumber}</span>
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your Name *
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter your full name"
                  disabled={loading}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Enter OTP Code *
                </label>
                <input
                  type="text"
                  maxLength="6"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                  disabled={loading}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-center text-2xl tracking-widest font-semibold focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                <p className="mt-2 text-xs text-gray-500 text-center">
                  Enter the 6-digit code sent to your phone
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleCloseModal}
                  disabled={loading}
                  className="flex-1 px-4 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={handleVerifyOtp}
                  disabled={loading || otp.length !== 6 || !name.trim()}
                  className="flex-1 px-4 py-3 bg-orange-600 text-white rounded-lg font-semibold hover:bg-orange-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition"
                >
                  {loading ? 'Verifying...' : 'Verify & Submit'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/55 backdrop-blur-[2px] flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-7 text-white text-center">
              <div className="mx-auto w-16 h-16 rounded-full bg-white/20 border border-white/40 flex items-center justify-center mb-4">
                <svg className="w-9 h-9" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold leading-tight">Lead Submitted Successfully</h3>
              <p className="text-orange-50 text-sm mt-2">
                Your instant quote request has been received.
              </p>
            </div>

            <div className="px-6 py-6 text-center">
              <p className="text-gray-700 leading-relaxed">
                Our team will connect you with verified movers shortly.
              </p>
              <button
                onClick={handleCloseSuccessModal}
                className="mt-5 inline-flex items-center justify-center w-full px-5 py-3 rounded-xl bg-orange-600 text-white font-semibold hover:bg-orange-700 transition"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default CTA;
